import React from 'react';
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import type { SortingState } from '@tanstack/react-table';
import type { SxProps, Theme } from '@mui/material/styles';
import ColumnHeaderFilterIcon from '../common/ColumnHeaderFilterIcon';
import { SEARCE_COLORS } from '../../theme';
import type { BillingBudgetField } from '../../types/billing';
import {
  BILLING_FILTERABLE_COLUMNS,
  BILLING_SORTABLE_COLUMNS,
  BILLING_STICKY_COLUMN_IDS,
  getBillingColumnLabel,
  getMonthPrefixFromField,
  MONTH_DISPLAY_LABELS,
} from '../../utils/billingTableConfig';
import type { DataTableColumnDef } from '../table/dataTableTypes';
import type { BillingBudget } from '../../types/billing';
import { BILLING_TABLE_COLUMNS } from '../../types/billing';

function stickyLeftForField(field: BillingBudgetField): number {
  let left = 0;
  for (const col of BILLING_TABLE_COLUMNS) {
    if (col.id === field) break;
    if (BILLING_STICKY_COLUMN_IDS.includes(col.id)) {
      left += col.minWidth ?? 100;
    }
  }
  return left;
}

/** Each month is a single combined "spend / budget" column, so headers render as one flat row. */
function headerLabelForField(field: BillingBudgetField): string {
  const prefix = getMonthPrefixFromField(field);
  return prefix ? MONTH_DISPLAY_LABELS[prefix] : getBillingColumnLabel(field);
}

export interface SandboxBillingGroupedTableHeadProps {
  columns: DataTableColumnDef<BillingBudget>[];
  headerCellBaseSx: SxProps<Theme>;
  sorting: SortingState;
  onHeaderSortClick: (columnId: BillingBudgetField) => void;
  onFilterClick: (columnId: BillingBudgetField, e: React.MouseEvent<HTMLElement>) => void;
  isFilterActive: (columnId: BillingBudgetField) => boolean;
}

export function SandboxBillingGroupedTableHead({
  columns,
  headerCellBaseSx,
  sorting,
  onHeaderSortClick,
  onFilterClick,
  isFilterActive,
}: SandboxBillingGroupedTableHeadProps): React.ReactElement {
  const renderSortLabel = (label: string, field: BillingBudgetField, center = false) => {
    const sortable = BILLING_SORTABLE_COLUMNS.includes(field);
    const sorted = sorting[0]?.id === field;
    return (
      <Typography
        component="span"
        onClick={sortable ? () => onHeaderSortClick(field) : undefined}
        sx={{
          fontWeight: 600,
          fontSize: '0.75rem',
          color: SEARCE_COLORS.white,
          cursor: sortable ? 'pointer' : 'default',
          display: 'block',
          textAlign: center ? 'center' : 'left',
        }}
      >
        {label}
        {sortable && sorted && (sorting[0]?.desc ? ' ↓' : ' ↑')}
      </Typography>
    );
  };

  const renderFilterableHeader = (field: BillingBudgetField, label: string, center: boolean) => {
    const filterable = BILLING_FILTERABLE_COLUMNS.includes(field);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: center ? 'center' : 'flex-start', gap: 0.25 }}>
        {renderSortLabel(label, field, center)}
        {filterable && (
          <Tooltip title={`Filter ${label}`}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onFilterClick(field, e);
              }}
              sx={{
                p: 0.25,
                color: SEARCE_COLORS.white,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <ColumnHeaderFilterIcon isFilterActive={isFilterActive(field)} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <TableRow>
      {columns.map((col) => {
        const field = col.id as BillingBudgetField;
        const isSticky = BILLING_STICKY_COLUMN_IDS.includes(field);
        const isMonth = Boolean(getMonthPrefixFromField(field));
        const width = (col.meta?.minWidth as number) ?? 100;
        return (
          <TableCell
            key={field}
            align={isMonth ? 'center' : 'left'}
            sx={[
              headerCellBaseSx as object,
              {
                minWidth: width,
                verticalAlign: 'bottom',
              },
              isSticky && {
                position: 'sticky',
                left: stickyLeftForField(field),
                zIndex: 13,
              },
            ]}
            onClick={
              BILLING_SORTABLE_COLUMNS.includes(field) ? () => onHeaderSortClick(field) : undefined
            }
          >
            {renderFilterableHeader(field, headerLabelForField(field), isMonth)}
          </TableCell>
        );
      })}
    </TableRow>
  );
}
